import { markProjectModelChanged } from '../project/project_revision_service.js';

const PLAN_LAYER_VISIBILITY_SCHEMA_VERSION = 1;
const PLAN_LAYER_GROUP_ID_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;
const PLAN_LAYER_ID_PATTERN = /^[a-z0-9][a-z0-9_-]*(?:\.[a-z0-9][a-z0-9_-]*)+$/;

export const PLAN_LAYER_GROUP_IDS = Object.freeze({
  FLOORPLANS: 'floorplans',
  OBSTACLES: 'obstacles',
  OBJECTS: 'objects',
  SYSTEMS: 'systems',
  ANNOTATIONS: 'annotations'
});

export const PLAN_LAYER_IDS = Object.freeze({
  FLOORPLANS_PDF: 'floorplans.pdf',
  FLOORPLANS_IMAGE: 'floorplans.image',
  FLOORPLANS_DXF: 'floorplans.dxf',
  FLOORPLANS_SATELLITE: 'floorplans.satellite',
  OBSTACLES_PIPES: 'obstacles.pipes',
  OBSTACLES_VENTILATION: 'obstacles.ventilation',
  OBSTACLES_PARAMETRIC_WALLS: 'obstacles.parametric-walls',
  OBJECTS_DEFAULT: 'objects.default',
  ANNOTATIONS_AREA_MEASUREMENTS: 'annotations.area-measurements',
  ANNOTATIONS_LINEAR_DIMENSIONS: 'annotations.linear-dimensions',
  ANNOTATIONS_TEXT: 'annotations.text'
});

export const PLAN_LAYER_GROUP_DEFINITIONS = deepFreeze([
  { id: PLAN_LAYER_GROUP_IDS.FLOORPLANS, label: 'Grundriss', order: 100, defaultVisible: true, source: 'core' },
  { id: PLAN_LAYER_GROUP_IDS.OBSTACLES, label: 'Hindernisse', order: 200, defaultVisible: true, source: 'core' },
  { id: PLAN_LAYER_GROUP_IDS.OBJECTS, label: 'Objekte', order: 300, defaultVisible: true, source: 'core' },
  { id: PLAN_LAYER_GROUP_IDS.SYSTEMS, label: 'Systeme', order: 400, defaultVisible: true, source: 'core' },
  { id: PLAN_LAYER_GROUP_IDS.ANNOTATIONS, label: 'Bemassungen & Beschriftungen', order: 500, defaultVisible: true, source: 'core' }
]);

export const CORE_PLAN_LAYER_DEFINITIONS = deepFreeze([
  {
    id: PLAN_LAYER_IDS.FLOORPLANS_PDF,
    groupId: PLAN_LAYER_GROUP_IDS.FLOORPLANS,
    label: 'PDF-Plan',
    order: 100,
    reserved: false,
    exposeAsChild: true,
    defaultVisible: true,
    source: 'core'
  },
  {
    id: PLAN_LAYER_IDS.FLOORPLANS_IMAGE,
    groupId: PLAN_LAYER_GROUP_IDS.FLOORPLANS,
    label: 'Bildplan',
    order: 110,
    reserved: false,
    exposeAsChild: true,
    defaultVisible: true,
    source: 'core'
  },
  {
    id: PLAN_LAYER_IDS.FLOORPLANS_DXF,
    groupId: PLAN_LAYER_GROUP_IDS.FLOORPLANS,
    label: 'DXF-Plan',
    order: 120,
    reserved: true,
    exposeAsChild: true,
    defaultVisible: true,
    source: 'core'
  },
  {
    id: PLAN_LAYER_IDS.FLOORPLANS_SATELLITE,
    groupId: PLAN_LAYER_GROUP_IDS.FLOORPLANS,
    label: 'Satellitenplan',
    order: 130,
    reserved: false,
    exposeAsChild: true,
    defaultVisible: true,
    source: 'core'
  },
  {
    id: PLAN_LAYER_IDS.OBSTACLES_PIPES,
    groupId: PLAN_LAYER_GROUP_IDS.OBSTACLES,
    label: 'Rohre',
    order: 200,
    reserved: false,
    exposeAsChild: true,
    defaultVisible: true,
    source: 'core'
  },
  {
    id: PLAN_LAYER_IDS.OBSTACLES_VENTILATION,
    groupId: PLAN_LAYER_GROUP_IDS.OBSTACLES,
    label: 'Lüftungen',
    order: 210,
    reserved: false,
    exposeAsChild: true,
    defaultVisible: true,
    source: 'core'
  },
  {
    id: PLAN_LAYER_IDS.OBSTACLES_PARAMETRIC_WALLS,
    groupId: PLAN_LAYER_GROUP_IDS.OBSTACLES,
    label: 'Parametrische Wände',
    order: 220,
    reserved: false,
    exposeAsChild: true,
    defaultVisible: true,
    source: 'core'
  },
  {
    id: PLAN_LAYER_IDS.OBJECTS_DEFAULT,
    groupId: PLAN_LAYER_GROUP_IDS.OBJECTS,
    label: 'Objekte',
    order: 300,
    reserved: false,
    exposeAsChild: false,
    defaultVisible: true,
    source: 'core'
  },
  {
    id: PLAN_LAYER_IDS.ANNOTATIONS_AREA_MEASUREMENTS,
    groupId: PLAN_LAYER_GROUP_IDS.ANNOTATIONS,
    label: 'Flächenbemassung',
    order: 500,
    reserved: false,
    exposeAsChild: true,
    defaultVisible: true,
    source: 'core'
  },
  {
    id: PLAN_LAYER_IDS.ANNOTATIONS_LINEAR_DIMENSIONS,
    groupId: PLAN_LAYER_GROUP_IDS.ANNOTATIONS,
    label: 'Lineare Bemassungen',
    order: 510,
    reserved: false,
    exposeAsChild: true,
    defaultVisible: true,
    source: 'core'
  },
  {
    id: PLAN_LAYER_IDS.ANNOTATIONS_TEXT,
    groupId: PLAN_LAYER_GROUP_IDS.ANNOTATIONS,
    label: 'Texte',
    order: 520,
    reserved: false,
    exposeAsChild: true,
    defaultVisible: true,
    source: 'core'
  }
]);

const CORE_PLAN_LAYER_BY_ID = new Map(CORE_PLAN_LAYER_DEFINITIONS.map(layer => [layer.id, layer]));

/**
 * Liefert die stabilen Gruppen der 2D-Zeichnung in der vorgesehenen UI-Reihenfolge.
 */
export function getPlanLayerGroups() {
  return PLAN_LAYER_GROUP_DEFINITIONS;
}

/**
 * Liefert alle vom Core definierten Layer. Systemlayer werden bewusst separat ergänzt.
 */
export function getCorePlanLayers() {
  return CORE_PLAN_LAYER_DEFINITIONS;
}

/**
 * Liefert Core- und dynamische Systemlayer ohne doppelte IDs.
 */
export function getPlanLayers(systems = []) {
  const layers = [...CORE_PLAN_LAYER_DEFINITIONS];
  const knownIds = new Set(layers.map(layer => layer.id));
  for (const system of Array.isArray(systems) ? systems : []) {
    const layer = planLayerForSystem(system);
    if (!layer || knownIds.has(layer.id)) continue;
    knownIds.add(layer.id);
    layers.push(layer);
  }
  return Object.freeze(layers);
}

/**
 * Sucht einen Core- oder dynamischen Systemlayer anhand seiner stabilen ID.
 */
export function getPlanLayer(id, systems = []) {
  const layerId = String(id || '').trim();
  if (!layerId) return null;
  return getPlanLayers(systems).find(layer => layer.id === layerId) || null;
}

/**
 * Bildet eine System-ID auf den reservierten Layer-Namensraum `systems.*` ab.
 */
export function systemPlanLayerId(systemId) {
  const segment = normalizeLayerSegment(systemId);
  return segment ? `${PLAN_LAYER_GROUP_IDS.SYSTEMS}.${segment}` : '';
}

/**
 * Ordnet den aktuell unterstützten Grundrissquellen ihren Layer zu.
 */
export function floorplanPlanLayerId(floorplan) {
  const explicitId = String(floorplan?.planLayerId || '').trim();
  if (explicitId.startsWith(`${PLAN_LAYER_GROUP_IDS.FLOORPLANS}.`) && isValidPlanLayerId(explicitId)) {
    return explicitId;
  }

  const descriptors = [
    floorplan?.kind,
    floorplan?.backgroundType,
    floorplan?.type,
    floorplan?.format,
    floorplan?.mimeType,
    floorplan?.provider,
    floorplan?.name,
    floorplan?.fileName,
    floorplan?.sourceName
  ].map(value => String(value || '').trim().toLowerCase());
  const descriptor = descriptors.join(' ');

  if (descriptor.includes('satellite')
    || descriptor.includes('satellit')
    || descriptors.includes('esri')
    || descriptors.includes('swisstopo')) {
    return PLAN_LAYER_IDS.FLOORPLANS_SATELLITE;
  }
  if (/(^|[\s./_-])dxf($|[\s./_-])/.test(descriptor)) {
    return PLAN_LAYER_IDS.FLOORPLANS_DXF;
  }
  if (/(^|[\s./_-])pdf($|[\s./_-])/.test(descriptor)) {
    return PLAN_LAYER_IDS.FLOORPLANS_PDF;
  }
  return PLAN_LAYER_IDS.FLOORPLANS_IMAGE;
}

/**
 * Leitet die Layerdefinition eines registrierten Systems ab.
 * Verdeckte Core-Hilfssysteme erscheinen nur bei einer expliziten Zuordnung.
 */
export function planLayerForSystem(system) {
  if (!system || typeof system !== 'object') return null;
  if (system.meta?.planLayerId === false) return null;
  const explicitId = String(system.meta?.planLayerId || '').trim();
  if (explicitId) {
    if (!isValidPlanLayerId(explicitId)) return null;
    return CORE_PLAN_LAYER_BY_ID.get(explicitId) || createSystemLayerDefinition(system, explicitId);
  }
  if (system.hidden === true
    || system.hiddenFromNavigation === true
    || system.meta?.hidden === true
    || system.meta?.hiddenFromNavigation === true) return null;
  const id = systemPlanLayerId(system.id);
  return id ? createSystemLayerDefinition(system, id) : null;
}

/**
 * Liefert aus `gruppe.layer` die Gruppen-ID; ungültige IDs ergeben einen Leerstring.
 */
export function planLayerGroupIdForLayer(id) {
  const layerId = String(id || '').trim();
  if (!isValidPlanLayerId(layerId)) return '';
  return layerId.slice(0, layerId.indexOf('.'));
}

/**
 * Migriert die projektbezogenen Sichtbarkeiten auf Schema 1.
 * Gespeichert werden ausschliesslich `false`-Overrides; unbekannte gültige IDs bleiben erhalten.
 */
export function normalizeProjectPlanLayerVisibility(project) {
  const source = isRecord(project?.planLayerVisibility) ? project.planLayerVisibility : {};
  const normalized = {
    schemaVersion: PLAN_LAYER_VISIBILITY_SCHEMA_VERSION,
    groups: normalizeFalseOverrides(source.groups, isValidPlanLayerGroupId),
    layers: normalizeFalseOverrides(source.layers, isValidPlanLayerId)
  };
  if (isRecord(project)) project.planLayerVisibility = normalized;
  return normalized;
}

export function isPlanLayerGroupVisible(project, groupId) {
  const id = String(groupId || '').trim();
  if (!isValidPlanLayerGroupId(id)) return false;
  return project?.planLayerVisibility?.groups?.[id] !== false;
}

export function isPlanLayerVisible(project, layerId) {
  const id = String(layerId || '').trim();
  const groupId = planLayerGroupIdForLayer(id);
  if (!groupId || !isPlanLayerGroupVisible(project, groupId)) return false;
  return project?.planLayerVisibility?.layers?.[id] !== false;
}

export function setProjectPlanLayerGroupVisibility(project, groupId, visible) {
  const id = String(groupId || '').trim();
  if (!isRecord(project) || !isValidPlanLayerGroupId(id)) return false;
  const visibility = normalizeProjectPlanLayerVisibility(project);
  const changed = setFalseOverride(visibility.groups, id, visible !== false);
  if (changed) markProjectModelChanged('plan-layer-group-visibility');
  return changed;
}

export function setProjectPlanLayerVisibility(project, layerId, visible) {
  const id = String(layerId || '').trim();
  if (!isRecord(project) || !isValidPlanLayerId(id)) return false;
  const visibility = normalizeProjectPlanLayerVisibility(project);
  const changed = setFalseOverride(visibility.layers, id, visible !== false);
  if (changed) markProjectModelChanged('plan-layer-visibility');
  return changed;
}

/**
 * Standardattribute für ein SVG-Element, das zu einem konkreten Planlayer gehört.
 */
export function planLayerSvgAttributes(layerId, project, { role = 'content', attrs = {} } = {}) {
  const id = String(layerId || '').trim();
  const groupId = planLayerGroupIdForLayer(id);
  const visible = isPlanLayerVisible(project, id);
  return visibilitySvgAttributes(attrs, visible, {
    'data-plan-layer-id': id,
    'data-plan-layer-group-id': groupId,
    'data-plan-layer-role': normalizeRole(role, 'content')
  });
}

/**
 * Standardattribute für einen SVG-Container einer ganzen Planlayer-Gruppe.
 */
export function planLayerGroupSvgAttributes(groupId, project, { role = 'group', attrs = {} } = {}) {
  const id = String(groupId || '').trim();
  const visible = isPlanLayerGroupVisible(project, id);
  return visibilitySvgAttributes(attrs, visible, {
    'data-plan-layer-group-id': id,
    'data-plan-layer-role': normalizeRole(role, 'group')
  });
}

function createSystemLayerDefinition(system, id) {
  return Object.freeze({
    id,
    groupId: planLayerGroupIdForLayer(id),
    label: String(system.label || system.name || system.id || id),
    order: 1000,
    reserved: false,
    exposeAsChild: true,
    defaultVisible: true,
    source: 'system',
    systemId: String(system.id || '')
  });
}

function normalizeLayerSegment(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

function normalizeFalseOverrides(source, idValidator) {
  if (!isRecord(source)) return {};
  return Object.fromEntries(Object.entries(source).filter(([id, value]) => (
    value === false && idValidator(id)
  )));
}

function setFalseOverride(overrides, id, visible) {
  if (visible) {
    if (!Object.prototype.hasOwnProperty.call(overrides, id)) return false;
    delete overrides[id];
    return true;
  }
  if (overrides[id] === false) return false;
  overrides[id] = false;
  return true;
}

function visibilitySvgAttributes(attrs, visible, requiredAttrs) {
  const result = {
    ...(isRecord(attrs) ? attrs : {}),
    ...requiredAttrs,
    'data-plan-layer-visible': visible ? 'true' : 'false'
  };
  if (!visible) result.display = 'none';
  return result;
}

function normalizeRole(role, fallback) {
  const normalized = String(role || '').trim();
  return normalized || fallback;
}

function isValidPlanLayerGroupId(id) {
  return typeof id === 'string' && id.length <= 100 && PLAN_LAYER_GROUP_ID_PATTERN.test(id);
}

function isValidPlanLayerId(id) {
  return typeof id === 'string' && id.length <= 200 && PLAN_LAYER_ID_PATTERN.test(id);
}

function isRecord(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const entry of Object.values(value)) deepFreeze(entry);
  return Object.freeze(value);
}
